// 2. External libraries
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

// 4. Type imports
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { OAuthClientSummary } from '~/api/oauth';

// 5. Custom hooks and utilities
import {
  deleteOauthClientV1OauthClientsClientIdDelete,
  listOauthClientsV1OauthClientsGet,
} from '~/generated/api/oauth/oauth';
import { queryKeys } from './queryKeys';

/**
 * Query hook for fetching user's authorized OAuth apps.
 *
 * Fetches all active OAuth clients from GET /v1/oauth/clients endpoint.
 * Returns array of clients with topic details and permissions.
 * Automatically refetches on window focus per TanStack Query defaults.
 *
 * Per §TS.5.1: Full generic type parameters for TanStack Query.
 *
 * @returns UseQueryResult containing array of authorized OAuth clients.
 *
 * @example
 * ```tsx
 * function OAuthConsentCard({ clientName }: { clientName: 'chatgpt' | 'claude' }) {
 *   const { data: clients, isLoading, error } = useAuthorizedAppsQuery();
 *
 *   if (isLoading) return <Skeleton />;
 *
 *   const existingClient = clients?.find(c => c.clientType === clientName) ?? null;
 *
 *   if (existingClient) {
 *     return <ExistingClientInfo client={existingClient} />;
 *   }
 *   return <NewClientAuthForm />;
 * }
 * ```
 */
export function useAuthorizedAppsQuery(): UseQueryResult<OAuthClientSummary[], Error> {
  return useQuery<OAuthClientSummary[], Error, OAuthClientSummary[], readonly [string]>({
    queryKey: queryKeys.authorizedApps,
    queryFn: () => listOauthClientsV1OauthClientsGet(),
    retry: (failureCount, error) => {
      // Don't retry on auth errors (401/403); retry other failures up to twice.
      return (
        failureCount < 2 &&
        !(axios.isAxiosError(error) && [401, 403].includes(error.response?.status ?? 0))
      );
    },
  });
}

/**
 * Mutation hook for revoking OAuth application access.
 *
 * Deletes OAuth client via DELETE /v1/oauth/clients/{client_id}.
 * Invalidates authorized apps cache on success.
 *
 * Per §TS.5.1: Full generic type parameters for TanStack Query mutation.
 *
 * @returns UseMutationResult with void success type and string mutation parameter.
 *
 * @example
 * ```tsx
 * function RevokeApplicationDialog({ clientId }: { clientId: string }) {
 *   const revokeMutation = useRevokeAppMutation();
 *
 *   const handleRevoke = () => {
 *     revokeMutation.mutate(clientId, {
 *       onSuccess: () => {
 *         toast.success('Application access revoked');
 *       },
 *       onError: (error) => {
 *         toast.error(error.message);
 *       },
 *     });
 *   };
 *
 *   return <Button onClick={handleRevoke}>Revoke Access</Button>;
 * }
 * ```
 */
export function useRevokeAppMutation(): UseMutationResult<void, Error, string, unknown> {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string, unknown>({
    mutationFn: (clientId: string) => deleteOauthClientV1OauthClientsClientIdDelete(clientId),
    onSuccess: () => {
      // Invalidate authorized apps list to refetch without revoked client
      void queryClient.invalidateQueries({ queryKey: queryKeys.authorizedApps });
    },
  });
}
